/**
 * Drama Badge View (Web)
 *
 * Animated badge reveal showing drama score and category.
 * Shared across all animation variants.
 */

import React, { memo } from 'react';
import { Text, Animated } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useTranslation } from '@dramadetector/shared';
import { styles } from './animationStyles.web';

interface DramaBadgeViewProps {
  analysisResult: { score: number; category?: string } | undefined;
  badgeOpacity: Animated.Value;
  badgeScale: Animated.Value;
  categoryStyle: { gradient: readonly [string, string, ...string[]]; label: string };
}

const DramaBadgeView = memo(function DramaBadgeView({
  analysisResult,
  badgeOpacity,
  badgeScale,
  categoryStyle,
}: DramaBadgeViewProps) {
  const { t } = useTranslation('home');
  if (!analysisResult) return null;

  const category = analysisResult.category ?? 'caution';
  const label = t(`home.category_label.${category}`, { defaultValue: categoryStyle.label });

  return (
    <Animated.View
      style={[
        styles.badgeContainer,
        {
          opacity: badgeOpacity,
          transform: [
            {
              scale: badgeScale.interpolate({
                inputRange: [0, 0.5, 1],
                outputRange: [0, 1.2, 1],
              }),
            },
          ],
        },
      ]}
    >
      <LinearGradient colors={categoryStyle.gradient} style={styles.badge}>
        <Text style={styles.scoreText}>{analysisResult.score}</Text>
        <Text style={styles.categoryLabel}>{label}</Text>
      </LinearGradient>
    </Animated.View>
  );
});

export default DramaBadgeView;
